import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { db } from "../js/firebase";
import {
  collection,
  query,
  getDocs,
  orderBy,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot
} from "firebase/firestore";
import { logger } from '../services/loggerService';
import { getContrastColor } from '@/utils/colorUtils';
import { companyService } from '@/services/companyService';
import { companyCache } from '@/services/companyCache';
import { createLibraryAsset, deleteBunnyAssetById } from '@/services/bunnyUploadService';
import { LIBRARY_ASSET_TYPE } from '@/utils/bunnyConstants';
import { PROJECT_STATUS } from '@/utils/projectUtils';
import { valuesEqual } from '@/utils/valueCompare';

async function uploadCompanyLogoToBunny(file, companyId, companyName) {
  const { assetId, url } = await createLibraryAsset({
    file,
    companyId,
    companyName,
    assetType: LIBRARY_ASSET_TYPE.BRAND_ASSETS,
    tags: ['brand', 'logo'],
  });
  return { logoUrl: url, logoAssetId: assetId };
}

export const useAdminCompaniesStore = defineStore("adminCompanies", () => {
  // State
  const companies = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const points = ref([]);
  const initialized = ref(false);
  const currentCompany = ref(null);

  // Real-time listener unsubscribe function
  let unsubscribeCompanies = null;

  // Getters
  const activeCompanies = computed(() => 
    companies.value
      .filter(company => company.active)
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  const inactiveCompanies = computed(() => 
    companies.value
      .filter(company => !company.active)
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  // Add a new computed getter for finding companies by ID
  const getCompanyById = computed(() => (id) => {
    return companies.value.find(company => company.id === id) || null;
  });

  // Add a computed getter for finding point plans by ID
  const getPointPlansById = computed(() => (id) => {
    return points.value.find(pointPlan => pointPlan.id === id) || null;
  });

  // Get a specific company by ID
  const getCompany = async (companyId) => {
    if (!companyId) {
      logger.warn('[AdminCompanies] Company lookup attempted without ID');
      return null;
    }

    loading.value = true;
    error.value = null;

    try {
      logger.info('[AdminCompanies] Loading company data', { companyId });
      const companyRef = doc(db, "companies", companyId);
      const companyDoc = await getDoc(companyRef);

      if (companyDoc.exists()) {
        const companyData = {
          id: companyDoc.id,
          ...companyDoc.data(),
        };
        currentCompany.value = companyData;
        // Add to companies array if not already there
        if (!companies.value.find(c => c.id === companyId)) {
          companies.value = [...companies.value, companyData];
        }
        logger.info('[AdminCompanies] Company data loaded successfully', { companyId });
        return companyData;
      } else {
        logger.error('[AdminCompanies] Company not found', { companyId });
        error.value = 'Company not found';
        currentCompany.value = null;
        return null;
      }
    } catch (e) {
      logger.error('[AdminCompanies] Failed to get company:', {
        error: e.message,
        companyId
      });
      error.value = e.message;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  // Actions
  /**
   * Subscribe to real-time updates for all companies from Firestore
   */
  function subscribeToCompanies() {
    // Unsubscribe from existing listener if any
    if (unsubscribeCompanies) {
      unsubscribeCompanies();
      unsubscribeCompanies = null;
    }

    loading.value = true;
    error.value = null;

    try {
      const companiesRef = collection(db, "companies");
      const q = query(companiesRef, orderBy("name"));

      unsubscribeCompanies = onSnapshot(q, (querySnapshot) => {
        const fetchedCompanies = [];
        querySnapshot.forEach((doc) => {
          const companyData = doc.data();

          // Normalize renewal date format
          if (companyData.renewalDate) {
            // Convert any timestamp or date string to ISO string format
            try {
              const dateObj = new Date(companyData.renewalDate);
              if (!isNaN(dateObj.getTime())) {
                companyData.renewalDate = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
              }
            } catch (e) {
              logger.warn('[AdminCompanies] Date normalization failed', {
                company: companyData.name,
                error: e.message
              });
            }
          }

          fetchedCompanies.push({
            id: doc.id,
            ...companyData,
          });
        });

        companies.value = fetchedCompanies;
        loading.value = false;
        logger.info('[AdminCompanies] Companies updated via real-time listener', {
          count: fetchedCompanies.length
        });
      }, (e) => {
        logger.firestoreListenerError('[AdminCompanies] Error in companies listener:', e);
        error.value = e.message;
        loading.value = false;
      });
    } catch (e) {
      logger.error('[AdminCompanies] Error setting up companies listener:', {
        error: e.message
      });
      error.value = e.message;
      loading.value = false;
    }
  }

  /**
   * Fetch all companies from Firestore (one-time fetch, kept for backward compatibility)
   */
  async function getAllCompanies() {
    loading.value = true;
    error.value = null;

    try {
      const companiesRef = collection(db, "companies");
      const q = query(companiesRef, orderBy("name"));
      const querySnapshot = await getDocs(q);

      const fetchedCompanies = [];
      querySnapshot.forEach((doc) => {
        const companyData = doc.data();

        // Normalize renewal date format
        if (companyData.renewalDate) {
          // Convert any timestamp or date string to ISO string format
          try {
            const dateObj = new Date(companyData.renewalDate);
            if (!isNaN(dateObj.getTime())) {
              companyData.renewalDate = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
            }
          } catch (e) {
            logger.warn('[AdminCompanies] Date normalization failed', {
              company: companyData.name,
              error: e.message
            });
          }
        }

        fetchedCompanies.push({
          id: doc.id,
          ...companyData,
        });
      });

      companies.value = fetchedCompanies;
    } catch (e) {
      logger.error('[AdminCompanies] Error fetching companies:', {
        error: e.message
      });
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Unsubscribe from real-time listeners
   */
  function unsubscribe() {
    if (unsubscribeCompanies) {
      unsubscribeCompanies();
      unsubscribeCompanies = null;
      logger.info('[AdminCompanies] Unsubscribed from companies listener');
    }
  }

  // Initialize the store
  async function init() {
    if (initialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      logger.info('[AdminCompanies] Starting initialization with real-time listener');
      // Use real-time subscription instead of one-time fetch
      subscribeToCompanies();
      await getPointPlans();
      initialized.value = true;
      logger.info('[AdminCompanies] Initialization complete');
    } catch (err) {
      logger.error('[AdminCompanies] Error initializing admin companies store:', {
        error: err.message
      });
    } finally {
      loading.value = false;
    }
  }

  // Get point plans
  async function getPointPlans() {
    loading.value = true;
    try {
      const pointsCollection = collection(db, "points");
      const pointsSnapshot = await getDocs(pointsCollection);
      points.value = pointsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('[AdminCompanies] Error getting point plans:', {
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  // Add company
  async function addCompany(company) {
    loading.value = true;
    try {
      logger.info('[AdminCompanies] Adding new company', {
        companyName: company.name
      });

      // Create the company doc FIRST so the Bunny upload (which reads
      // companies/{id} for the path prefix) finds the record.
      const { logoFile, id: _id, ...companyData } = company;

      const saveData = {
        ...companyData,
        logoUrl: '',
        logoAssetId: null,
        createdAt: new Date().toISOString(),
      };

      if (companyData.brandColor) {
        saveData.brandTextColor = getContrastColor(companyData.brandColor);
      }

      const companyRef = doc(collection(db, 'companies'));
      await setDoc(companyRef, saveData);

      // Now that the company exists, upload the logo (if any) as a
      // library asset and link it back.
      if (logoFile) {
        try {
          const { logoUrl, logoAssetId } = await uploadCompanyLogoToBunny(
            logoFile, companyRef.id, companyData.name,
          );
          await updateDoc(companyRef, { logoUrl, logoAssetId });
        } catch (err) {
          logger.warn('[AdminCompanies] Logo upload failed during add', {
            companyId: companyRef.id, error: err.message,
          });
        }
      }

      await companyService.logActivity(
        companyRef.id, 'created', null, null, null, null,
      );

      return companyRef.id;
    } catch (error) {
      logger.error('[AdminCompanies] Failed to add company:', {
        error: error.message,
        companyName: company.name
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  // Edit company
  async function editCompany(company) {
    loading.value = true;
    try {
      logger.info('[AdminCompanies] Updating company', { companyId: company.id });

      // Read the current company doc to find the existing logoAssetId
      // (needed to clean up old logo on remove/replace).
      const currentSnap = await getDoc(doc(db, 'companies', company.id));
      const existingLogoAssetId = currentSnap.exists()
        ? currentSnap.data().logoAssetId || null
        : null;

      let logoUrl = company.existingLogoUrl || '';
      let logoAssetId = existingLogoAssetId;

      if (company.logoUrl === null && company.existingLogoUrl) {
        // Logo explicitly removed.
        logger.info('[AdminCompanies] Logo removal detected', { companyId: company.id });
        await deleteBunnyAssetById(existingLogoAssetId);
        logoUrl = null;
        logoAssetId = null;
      } else if (company.logoFile) {
        // Replacing the logo. Clean up the old asset first so we don't
        // leave an orphaned brand-asset record in the library.
        await deleteBunnyAssetById(existingLogoAssetId);
        try {
          const result = await uploadCompanyLogoToBunny(
            company.logoFile, company.id, company.name,
          );
          logoUrl = result.logoUrl;
          logoAssetId = result.logoAssetId;
        } catch (err) {
          logger.warn('[AdminCompanies] Logo upload failed during edit', {
            companyId: company.id, error: err.message,
          });
          // Keep the previous logoUrl/Id values so the company isn't
          // left with broken pointers if the new upload failed.
        }
      }
      
      // Update company doc without the file objects
      const { logoFile: _logoFile, existingLogoUrl: _existingLogoUrl, ...companyData } = company;
      
      // Calculate contrast color if brandColor is provided
      const updateData = {
        ...companyData,
        logoUrl,
        logoAssetId,
        updatedAt: new Date().toISOString()
      };

      if (companyData.brandColor) {
        updateData.brandTextColor = getContrastColor(companyData.brandColor);
      }
      
      const companyRef = doc(db, "companies", company.id);

      // Get the current company data to compare changes
      const currentDoc = await getDoc(companyRef);
      const currentData = currentDoc.exists() ? currentDoc.data() : {};

      // Update the document
      await updateDoc(companyRef, updateData);

      // Invalidate the cached company so impersonation/navbar re-fetch the
      // fresh logo+theme instead of the stale localStorage copy.
      companyCache.removeCompany(company.id);

      // Log changes for tracked fields
      const trackedFields = ['name', 'logoUrl', 'brandColor', 'active', 'stripeCustomerId', 'vysualOnly', 'conversationsEnabled', 'freeBasicEligible'];
      for (const field of trackedFields) {
        const oldValue = currentData[field];
        const newValue = updateData[field];

        // Only log if the value actually changed. valuesEqual covers empty-vs-empty
        // and Timestamp-vs-Date, which a bare !== reports as phantom changes.
        if (newValue !== undefined && !valuesEqual(oldValue, newValue)) {
          await companyService.logActivity(
            company.id,
            'field_update',
            field,
            oldValue,
            newValue,
            null
          );
        }
      }

      // Real-time listener will automatically update companies list
    } catch (error) {
      logger.error('[AdminCompanies] Failed to edit company:', {
        error: error.message,
        companyId: company.id
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  // PORT FROM storeCompanies.js: Calculate remaining points
  function calculateRemainingPoints(company, projects) {
    // Add validation with detailed logging
    if (!company?.renewalDate || !company?.totalPoints) {
      logger.warn('[AdminCompanies] Invalid company data for calculation', {
        companyId: company?.id,
        companyName: company?.name,
        hasRenewalDate: !!company?.renewalDate,
        hasTotalPoints: !!company?.totalPoints,
        totalPoints: company?.totalPoints
      });
      return 0;
    }
    
    const { soc, eoc } = calculateContractPeriod(company.renewalDate);
    const relevantProjects = filterProjectsInPeriod(projects, company.id, soc, eoc);
    const usedPoints = sumProjectDays(relevantProjects, company);
    const remainingPoints = company.totalPoints - usedPoints;
    
    // Add debug logging for troubleshooting
    logger.debug('[AdminCompanies] Points calculation completed', {
      companyId: company.id,
      companyName: company.name,
      contractPeriod: { 
        soc: soc.toISOString().split('T')[0], 
        eoc: eoc.toISOString().split('T')[0] 
      },
      relevantProjectsCount: relevantProjects.length,
      relevantProjects: relevantProjects.map(p => ({
        id: p.id,
        name: p.name,
        daysCount: p.daysCount,
        pointsTotal: p.pointsTotal,
        status: p.status
      })),
      usedPoints,
      totalPoints: company.totalPoints,
      remainingPoints,
      companyType: company.days ? 'days' : 'hours'
    });
    
    return remainingPoints;
  }

  // PORT FROM storeCompanies.js: Calculate contract period
  function calculateContractPeriod(renewalDate) {
    let renewalDateObj;
    
    // Handle different possible formats
    if (typeof renewalDate === 'number') {
      // Handle timestamp in milliseconds
      renewalDateObj = new Date(renewalDate);
    } else if (typeof renewalDate === 'string') {
      // If it's a string with just a date (YYYY-MM-DD), ensure proper timezone handling
      if (renewalDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // For date-only strings, use UTC to avoid timezone issues
        const [year, month, day] = renewalDate.split('-').map(Number);
        renewalDateObj = new Date(Date.UTC(year, month - 1, day));
      } else {
        // Handle full ISO string or other string formats
        renewalDateObj = new Date(renewalDate);
      }
    } else {
      // Fallback for other cases
      renewalDateObj = new Date(renewalDate);
    }
    
    // Verify the date is valid
    if (isNaN(renewalDateObj.getTime())) {
      logger.error('[AdminCompanies] Invalid date from:', {
        date: renewalDate
      });
      return { soc: new Date(), eoc: new Date() }; // Return current date as fallback
    }
    
    const year = renewalDateObj.getFullYear();
    const month = renewalDateObj.getMonth();
    const day = renewalDateObj.getDate();
    
    // Start of contract period (one year before renewal)
    const soc = new Date(year - 1, month, day);
    
    // End of contract period (renewal date)
    const eoc = new Date(year, month, day);
    
    return { soc, eoc };
  }

  // PORT FROM storeCompanies.js: Filter projects in period
  function filterProjectsInPeriod(projects, companyId, startDate, endDate) {
    // Handle case when projects is undefined or null
    if (!projects || !Array.isArray(projects)) {
      logger.warn('[AdminCompanies] Projects data is not available for filtering', {
        companyId,
        projectsType: typeof projects,
        projectsLength: projects?.length
      });
      return [];
    }
    
    const filteredProjects = projects.filter(project => {
      // Check if project belongs to this company
      const companyMatch = project.company === companyId;
      if (!companyMatch) {
        return false;
      }
      
      // Exclude edit projects from calculations
      if (project.type === "edit") {
        logger.debug('[AdminCompanies] Excluding edit project from calculation', {
          projectId: project.id,
          projectName: project.name,
          projectType: project.type,
          companyId
        });
        return false;
      }
      
      // Improved date handling - prioritize dateSubmitted but log discrepancies
      let projectDate = null;
      if (project.dateSubmitted) {
        projectDate = new Date(project.dateSubmitted);
        // Log if dateCreated exists and differs significantly
        if (project.dateCreated) {
          const createdDate = new Date(project.dateCreated);
          const timeDiff = Math.abs(projectDate.getTime() - createdDate.getTime());
          const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
          if (daysDiff > 1) {
            logger.debug('[AdminCompanies] Date discrepancy found', {
              projectId: project.id,
              dateSubmitted: project.dateSubmitted,
              dateCreated: project.dateCreated,
              daysDifference: Math.round(daysDiff)
            });
          }
        }
      } else if (project.dateCreated) {
        projectDate = new Date(project.dateCreated);
        logger.debug('[AdminCompanies] Using dateCreated (no dateSubmitted)', {
          projectId: project.id,
          dateCreated: project.dateCreated
        });
      } else {
        logger.warn('[AdminCompanies] Project missing both date fields', {
          projectId: project.id,
          projectName: project.name,
          companyId
        });
        return false;
      }
      
      // Validate the date
      if (!projectDate || isNaN(projectDate.getTime())) {
        logger.warn('[AdminCompanies] Invalid project date', {
          projectId: project.id,
          dateSubmitted: project.dateSubmitted,
          dateCreated: project.dateCreated
        });
        return false;
      }
      
      const inPeriod = projectDate >= startDate && projectDate <= endDate;
      
      if (!inPeriod) {
        logger.debug('[AdminCompanies] Project outside contract period', {
          projectId: project.id,
          projectDate: projectDate.toISOString().split('T')[0],
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        });
      }
      
      return inPeriod;
    });
    
    logger.debug('[AdminCompanies] Project filtering completed', {
      companyId,
      totalProjects: projects.length,
      filteredProjects: filteredProjects.length,
      contractPeriod: {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      }
    });
    
    return filteredProjects;
  }

  // PORT FROM storeCompanies.js: Sum project days/hours
  function sumProjectDays(projects, company) {
    if (!company) {
      logger.warn('[AdminCompanies] Company data missing for points calculation');
      return 0;
    }
    
    const isHoursBased = company.days === false;
    const companyType = isHoursBased ? 'hours' : 'days';
    
    logger.debug('[AdminCompanies] Starting points summation', {
      companyId: company.id,
      companyType,
      projectCount: projects.length
    });
    
    return projects.reduce((total, project) => {
      let value = null;
      let fallbackValue = null;
      let valueSource = null;
      
      // Always use daysCount as the primary metric for both days and hours
      if (project.daysCount !== undefined && project.daysCount !== null) {
        const daysValue = Number(project.daysCount);
        value = isHoursBased ? daysValue * 8 : daysValue; // Convert to hours if needed
        valueSource = isHoursBased ? 'daysCount (converted to hours)' : 'daysCount';
      }
      
      // Set fallback values
      fallbackValue = isHoursBased ? 8 : 1; // 8 hours or 1 day
      
      // Use the value if it's valid
      if (!isNaN(value) && value > 0) {
        logger.debug('[AdminCompanies] Using project value', {
          projectId: project.id,
          projectName: project.name,
          value,
          valueSource,
          companyType
        });
        return total + value;
      }
      
      // Apply fallback logic for completed projects without proper values
      const isCompleted = project.status === PROJECT_STATUS.COMPLETED ||
                         project.status === "completed" ||
                         project.status === "Complete";
      
      if (isCompleted) {
        logger.debug('[AdminCompanies] Using fallback value for completed project', {
          projectId: project.id,
          projectName: project.name,
          status: project.status,
          fallbackValue,
          companyType,
          availableFields: {
            daysCount: project.daysCount,
            pointsTotal: project.pointsTotal
          }
        });
        return total + fallbackValue;
      }
      
      // Log when projects are excluded from calculation
      logger.debug('[AdminCompanies] Project excluded from calculation', {
        projectId: project.id,
        projectName: project.name,
        status: project.status,
        daysCount: project.daysCount,
        pointsTotal: project.pointsTotal,
        reason: 'Not completed and no valid point value'
      });
      
      return total;
    }, 0);
  }

  // Add a helper function to format dates consistently
  function formatDateForDisplay(dateValue) {
    if (!dateValue) return '';
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue; // Return original if invalid
      
      // Format as MM/DD/YYYY
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch (e) {
      logger.warn('Error formatting date:', {
        error: e.message
      });
      return dateValue;
    }
  }

  /**
   * Delete a company (preflight check or actual deletion).
   * @param {String} companyId - Company ID to delete
   * @param {Boolean} dryRun - If true, only returns dependency counts
   * @returns {Object} - { canDelete, counts, deleted?, companyName }
   */
  async function deleteCompany(companyId, dryRun = true) {
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('@/js/firebase');
    const deleteCompanyFn = httpsCallable(functions, 'deleteCompany');
    const result = await deleteCompanyFn({ companyId, dryRun });

    if (result.data.deleted) {
      companies.value = companies.value.filter(c => c.id !== companyId);
      logger.info('[AdminCompanies] Company deleted and removed from local state', { companyId });
    }

    return result.data;
  }

  // Helper function to get calculation summary for debugging
  function getCalculationSummary(companyId) {
    const company = companies.value.find(c => c.id === companyId);
    if (!company) {
      return { error: 'Company not found' };
    }

    // This would need access to projects store - for now return basic info
    return {
      companyId: company.id,
      companyName: company.name,
      totalPoints: company.totalPoints,
      renewalDate: company.renewalDate,
      companyType: company.days ? 'days' : 'hours',
      hasValidConfig: !!(company.renewalDate && company.totalPoints)
    };
  }

  return {
    // State
    companies,
    loading,
    error,
    points,
    initialized,
    currentCompany,

    // Getters
    activeCompanies,
    inactiveCompanies,
    getCompanyById,
    getPointPlansById,

    // Actions
    getAllCompanies,
    subscribeToCompanies,
    unsubscribe,
    init,
    getPointPlans,
    addCompany,
    editCompany,
    getCompany,
    calculateRemainingPoints,
    calculateContractPeriod,
    filterProjectsInPeriod,
    sumProjectDays,
    formatDateForDisplay,
    getCalculationSummary,
    deleteCompany,
  };
});